import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { X, Link2, Upload, Loader2, Sparkles, AlertCircle, CheckCircle2 } from "lucide-react";

/* ───────── Shopify Import Modal ─────────
 * Two-step flow:
 *   1. Preview — paste a Shopify URL OR upload a CSV. Backend maps the
 *      catalog into Myntra-shaped products and returns a list with a
 *      per-product "% auto-filled" score.
 *   2. Selection — seller ticks the products they want, types sizes per
 *      product (Free Size is the default if blank), and clicks Import.
 *      Backend downloads images from the Shopify CDN and returns the
 *      products with `images` keyed by Myntra column → asset object.
 * On success we call `onImported(products)` which the AutoListingQuick
 * page uses to populate `pendingProducts`. The seller then clicks
 * Generate Excel as usual — no further uploads, no re-keying.
 */
export default function ShopifyImportModal({ open, onClose, onImported }) {
  const [tab, setTab] = useState("url"); // "url" | "csv"
  const [shopifyUrl, setShopifyUrl] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("input"); // input | review | ingesting
  const [preview, setPreview] = useState(null); // { import_id, products, ... }
  const [selections, setSelections] = useState({}); // handle -> { selected, sizes, mrp, isp }
  const [search, setSearch] = useState("");
  // Bulk-apply bar state — one row of inputs that the seller can stamp
  // onto every selected product with a single click.
  const [bulkSizes, setBulkSizes] = useState("Free Size");
  const [bulkMrp, setBulkMrp] = useState("");
  const [bulkIsp, setBulkIsp] = useState("");

  function reset() {
    setShopifyUrl("");
    setCsvFile(null);
    setPreview(null);
    setSelections({});
    setPhase("input");
    setLoading(false);
    setSearch("");
  }

  if (!open) return null;

  async function handlePreview() {
    if (tab === "url" && !shopifyUrl.trim()) {
      toast.error("Paste your Shopify store URL (e.g. organzza.shop)");
      return;
    }
    if (tab === "csv" && !csvFile) {
      toast.error("Pick a Shopify CSV export file");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      if (tab === "url") fd.append("shopify_url", shopifyUrl.trim());
      else fd.append("file", csvFile);
      const { data } = await api.post("/seller/shopify-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setPreview(data);
      // Default-select everything; seller can untick to skip.
      const initial = {};
      (data.products || []).forEach((p) => {
        initial[p.shopifyHandle] = {
          selected: true,
          sizes: "Free Size",
          mrp: p.mrp || "",
          isp: p.isp || "",
        };
      });
      setSelections(initial);
      setPhase("review");
      toast.success(`Imported ${data.total} products · avg ${data.avg_fill_score}% auto-filled`);
    } catch (e) {
      toast.error(errMsg(e, "Could not parse Shopify catalog"));
    } finally {
      setLoading(false);
    }
  }

  async function handleIngest() {
    if (!preview?.import_id) return;
    const picks = (preview.products || [])
      .filter((p) => selections[p.shopifyHandle]?.selected)
      .map((p) => {
        const s = selections[p.shopifyHandle];
        const sizes = (s?.sizes || "Free Size")
          .split(/[,;\n]/g)
          .map((x) => x.trim())
          .filter(Boolean);
        return {
          shopifyHandle: p.shopifyHandle,
          sizes,
          mrp: s?.mrp || p.mrp,
          isp: s?.isp || p.isp,
        };
      });
    if (!picks.length) {
      toast.error("Select at least one product");
      return;
    }
    if (picks.length > 1000) {
      toast.error("Max 1000 products per import — split into smaller batches");
      return;
    }
    setLoading(true);
    setPhase("ingesting");
    try {
      const { data } = await api.post(
        "/seller/shopify-import/ingest",
        { import_id: preview.import_id, selections: picks },
        { timeout: 180000 }
      );
      toast.success(`Imported ${data.count} products with images — ready to generate`);
      onImported?.(data.products || []);
      reset();
      onClose?.();
    } catch (e) {
      toast.error(errMsg(e, "Image download failed — please retry"));
      setPhase("review");
    } finally {
      setLoading(false);
    }
  }

  const filteredProducts = (preview?.products || []).filter((p) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (p.productDisplayName || "").toLowerCase().includes(q) ||
      (p.articleType || "").toLowerCase().includes(q) ||
      (p.brand || "").toLowerCase().includes(q)
    );
  });

  const selectedCount = Object.values(selections).filter((s) => s.selected).length;

  function applyBulkToSelected() {
    const next = { ...selections };
    let touched = 0;
    (preview?.products || []).forEach((p) => {
      const cur = next[p.shopifyHandle];
      if (!cur?.selected) return;
      next[p.shopifyHandle] = {
        ...cur,
        sizes: bulkSizes || cur.sizes || "Free Size",
        mrp: bulkMrp || cur.mrp,
        isp: bulkIsp || cur.isp,
      };
      touched += 1;
    });
    if (!touched) {
      toast.error("No products selected — tick some products first");
      return;
    }
    setSelections(next);
    toast.success(`Applied to ${touched} product${touched === 1 ? "" : "s"}`);
  }

  // Quick-pick size preset chips that auto-fill the bulk Sizes input.
  const SIZE_PRESETS = [
    { label: "Free Size", value: "Free Size" },
    { label: "S, M, L", value: "S, M, L" },
    { label: "S, M, L, XL", value: "S, M, L, XL" },
    { label: "S, M, L, XL, XXL", value: "S, M, L, XL, XXL" },
    { label: "XS–XXL", value: "XS, S, M, L, XL, XXL" },
    { label: "32, 34, 36, 38", value: "32, 34, 36, 38" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="shopify-import-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ color: "#0f172a" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center text-white">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-bold" style={{ color: "#0f172a" }}>Import from Shopify</div>
              <div className="text-xs" style={{ color: "#64748b" }}>
                {phase === "input" && "Paste store URL or upload Shopify CSV export"}
                {phase === "review" && `${preview?.total || 0} products · review & select`}
                {phase === "ingesting" && "Downloading images from Shopify CDN…"}
              </div>
            </div>
          </div>
          <button
            onClick={() => { reset(); onClose?.(); }}
            className="p-2 rounded-lg hover:bg-slate-100"
            style={{ color: "#64748b" }}
            data-testid="shopify-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {phase === "input" && (
            <div className="p-6 space-y-5">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTab("url")}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border ${
                    tab === "url" ? "bg-[#ECFDF5]" : "bg-white hover:bg-slate-50"
                  }`}
                  style={{
                    color: tab === "url" ? "#047857" : "#475569",
                    borderColor: tab === "url" ? "#34D399" : "#E2E8F0",
                  }}
                  data-testid="shopify-tab-url"
                >
                  <Link2 size={15} style={{ color: tab === "url" ? "#047857" : "#475569" }} />
                  <span>Shopify Store URL</span>
                </button>
                <button
                  onClick={() => setTab("csv")}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border ${
                    tab === "csv" ? "bg-[#ECFDF5]" : "bg-white hover:bg-slate-50"
                  }`}
                  style={{
                    color: tab === "csv" ? "#047857" : "#475569",
                    borderColor: tab === "csv" ? "#34D399" : "#E2E8F0",
                  }}
                  data-testid="shopify-tab-csv"
                >
                  <Upload size={15} style={{ color: tab === "csv" ? "#047857" : "#475569" }} />
                  <span>Upload CSV Export</span>
                </button>
              </div>

              {tab === "url" ? (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#334155" }}>
                    Shopify Store URL
                  </label>
                  <input
                    type="text"
                    value={shopifyUrl}
                    onChange={(e) => setShopifyUrl(e.target.value)}
                    placeholder="e.g. organzza.shop or yourstore.myshopify.com"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-emerald-400 outline-none text-sm"
                    style={{ color: "#0f172a", background: "#ffffff" }}
                    data-testid="shopify-url-input"
                    autoFocus
                  />
                  <div className="text-xs flex items-start gap-1.5" style={{ color: "#64748b" }}>
                    <AlertCircle size={12} className="mt-0.5 shrink-0" />
                    <span>
                      We fetch your catalog from <code className="bg-slate-100 px-1 rounded" style={{ color: "#334155" }}>/products.json</code> —
                      works on any public Shopify storefront. Up to 1000 products per import.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#334155" }}>
                    Shopify CSV Export
                  </label>
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center bg-slate-50">
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="shopify-csv-file"
                      data-testid="shopify-csv-input"
                    />
                    <label htmlFor="shopify-csv-file" className="cursor-pointer block">
                      <Upload size={28} className="mx-auto mb-2" style={{ color: "#94a3b8" }} />
                      <div className="text-sm font-semibold" style={{ color: "#334155" }}>
                        {csvFile ? csvFile.name : "Click to upload Shopify CSV"}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "#64748b" }}>
                        Shopify Admin → Products → Export → CSV (all products)
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <button
                onClick={handlePreview}
                disabled={loading}
                className="w-full px-5 py-3 rounded-lg hover:opacity-90 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg, #10B981 0%, #0D9488 100%)" }}
                data-testid="shopify-preview-btn"
              >
                {loading ? (<><Loader2 size={15} className="animate-spin" /> Loading catalog…</>) : (<>Preview Catalog →</>)}
              </button>
            </div>
          )}

          {phase === "review" && preview && (
            <div className="p-6 space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Total products" value={preview.total} accent="indigo" />
                <Stat label="Avg auto-filled" value={`${preview.avg_fill_score}%`} accent="emerald" />
                <Stat label="Selected" value={selectedCount} accent="orange" />
                <Stat label="Categories" value={Object.keys(preview.categories || {}).length} accent="purple" />
              </div>

              {/* Bulk apply bar — single-click sets sizes/MRP/Selling on
                  every selected product. Saves 100s of manual entries. */}
              <div className="rounded-xl border-2 p-4 space-y-3" style={{ borderColor: "#34D399", background: "#ECFDF5" }}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#065F46" }}>
                    Bulk apply to all selected ({selectedCount})
                  </span>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#065F46" }}>
                      Sizes (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={bulkSizes}
                      onChange={(e) => setBulkSizes(e.target.value)}
                      placeholder="S, M, L, XL"
                      className="w-full px-3 py-2 mt-1 rounded-lg border text-sm"
                      style={{ color: "#0f172a", borderColor: "#A7F3D0", background: "#ffffff" }}
                      data-testid="bulk-sizes-input"
                    />
                  </div>
                  <div className="w-28">
                    <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#065F46" }}>
                      MRP (₹)
                    </label>
                    <input
                      type="text"
                      value={bulkMrp}
                      onChange={(e) => setBulkMrp(e.target.value)}
                      placeholder="1999"
                      className="w-full px-3 py-2 mt-1 rounded-lg border text-sm"
                      style={{ color: "#0f172a", borderColor: "#A7F3D0", background: "#ffffff" }}
                      data-testid="bulk-mrp-input"
                    />
                  </div>
                  <div className="w-28">
                    <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#065F46" }}>
                      Selling (₹)
                    </label>
                    <input
                      type="text"
                      value={bulkIsp}
                      onChange={(e) => setBulkIsp(e.target.value)}
                      placeholder="1499"
                      className="w-full px-3 py-2 mt-1 rounded-lg border text-sm"
                      style={{ color: "#0f172a", borderColor: "#A7F3D0", background: "#ffffff" }}
                      data-testid="bulk-isp-input"
                    />
                  </div>
                  <button
                    onClick={applyBulkToSelected}
                    className="px-5 py-2 rounded-lg text-white font-bold text-sm hover:opacity-90 flex items-center gap-2"
                    style={{ background: "linear-gradient(135deg, #10B981 0%, #0D9488 100%)" }}
                    data-testid="bulk-apply-btn"
                  >
                    <Sparkles size={14} /> Apply to all
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider self-center" style={{ color: "#065F46" }}>
                    Quick presets:
                  </span>
                  {SIZE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => setBulkSizes(p.value)}
                      className="px-2.5 py-1 rounded-full text-[11px] font-semibold border hover:opacity-90"
                      style={{
                        background: bulkSizes === p.value ? "#10B981" : "#ffffff",
                        color: bulkSizes === p.value ? "#ffffff" : "#065F46",
                        borderColor: "#A7F3D0",
                      }}
                      data-testid={`bulk-size-preset-${p.label.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search + bulk actions */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products…"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-900"
                  data-testid="shopify-search"
                />
                <button
                  onClick={() => {
                    const next = { ...selections };
                    (preview.products || []).forEach((p) => {
                      next[p.shopifyHandle] = { ...next[p.shopifyHandle], selected: true };
                    });
                    setSelections(next);
                  }}
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
                  data-testid="shopify-select-all"
                >
                  Select All
                </button>
                <button
                  onClick={() => {
                    const next = { ...selections };
                    (preview.products || []).forEach((p) => {
                      next[p.shopifyHandle] = { ...next[p.shopifyHandle], selected: false };
                    });
                    setSelections(next);
                  }}
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
                  data-testid="shopify-clear-all"
                >
                  Clear
                </button>
              </div>

              {/* Product list */}
              <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
                {filteredProducts.slice(0, 200).map((p) => {
                  const sel = selections[p.shopifyHandle] || {};
                  return (
                    <div
                      key={p.shopifyHandle}
                      className="flex items-start gap-3 p-3 hover:bg-slate-50"
                      data-testid={`shopify-row-${p.shopifyHandle}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!sel.selected}
                        onChange={(e) =>
                          setSelections((prev) => ({
                            ...prev,
                            [p.shopifyHandle]: { ...prev[p.shopifyHandle], selected: e.target.checked },
                          }))
                        }
                        className="mt-1.5 w-4 h-4 accent-emerald-600"
                      />
                      {p.imageUrls?.[0] && (
                        <img
                          src={p.imageUrls[0]}
                          alt={p.productDisplayName}
                          className="w-14 h-14 object-cover rounded-lg border border-slate-200 shrink-0"
                          loading="lazy"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">
                          {p.productDisplayName}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          <span className="text-emerald-700 font-bold">{p.articleType}</span>
                          <span>{p.brand}</span>
                          <span>{p.imageUrls?.length || 0} images</span>
                          <span className={p.fillScore >= 60 ? "text-emerald-600" : "text-orange-600"}>
                            {p.fillScore}% filled
                          </span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            placeholder="Sizes (S,M,L)"
                            value={sel.sizes || ""}
                            onChange={(e) =>
                              setSelections((prev) => ({
                                ...prev,
                                [p.shopifyHandle]: { ...prev[p.shopifyHandle], sizes: e.target.value },
                              }))
                            }
                            className="w-32 px-2 py-1 text-xs rounded border border-slate-200 text-slate-900"
                            data-testid={`shopify-sizes-${p.shopifyHandle}`}
                          />
                          <input
                            type="text"
                            placeholder="MRP"
                            value={sel.mrp || ""}
                            onChange={(e) =>
                              setSelections((prev) => ({
                                ...prev,
                                [p.shopifyHandle]: { ...prev[p.shopifyHandle], mrp: e.target.value },
                              }))
                            }
                            className="w-20 px-2 py-1 text-xs rounded border border-slate-200 text-slate-900"
                            data-testid={`shopify-mrp-${p.shopifyHandle}`}
                          />
                          <input
                            type="text"
                            placeholder="Selling ₹"
                            value={sel.isp || ""}
                            onChange={(e) =>
                              setSelections((prev) => ({
                                ...prev,
                                [p.shopifyHandle]: { ...prev[p.shopifyHandle], isp: e.target.value },
                              }))
                            }
                            className="w-20 px-2 py-1 text-xs rounded border border-slate-200 text-slate-900"
                            data-testid={`shopify-isp-${p.shopifyHandle}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filteredProducts.length > 200 && (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    Showing first 200 — refine search to find specific products.
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                <div className="text-xs" style={{ color: "#64748b" }}>
                  {selectedCount} of {preview.total} selected · Max 1000 per ingest
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPhase("input")}
                    className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50"
                    data-testid="shopify-back-btn"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleIngest}
                    disabled={loading || !selectedCount}
                    className="px-5 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2"
                    style={{ background: "linear-gradient(135deg, #10B981 0%, #0D9488 100%)" }}
                    data-testid="shopify-ingest-btn"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Import {selectedCount} product{selectedCount === 1 ? "" : "s"} →
                  </button>
                </div>
              </div>
            </div>
          )}

          {phase === "ingesting" && (
            <div className="p-12 text-center">
              <Loader2 size={40} className="animate-spin mx-auto text-emerald-600 mb-4" />
              <div className="font-bold text-slate-900">Downloading images &amp; queuing products…</div>
              <div className="text-xs text-slate-500 mt-2">
                We&apos;re fetching each product&apos;s images from the Shopify CDN and saving them to your account.
                Don&apos;t close this window.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "indigo" }) {
  const colors = {
    indigo: "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-700",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700",
    orange: "from-orange-50 to-orange-100 border-orange-200 text-orange-700",
    purple: "from-purple-50 to-purple-100 border-purple-200 text-purple-700",
  };
  return (
    <div className={`rounded-xl p-3 bg-gradient-to-br border ${colors[accent] || colors.indigo}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
